import React, { useRef, useCallback } from "react";
import { theme } from "../../lib/theme";
import { VB_W, VB_H_FULL, VB_H_COMPACT } from "./constants";
import {
  computeLayout,
  renderBars,
  renderComparisonBars,
  renderGroupLabelsAndSeparators,
  useTooltip,
  TooltipOverlay,
} from "./rendering";
import {
  DesktopOnly,
  MobileOnly,
  ResponsiveSvg,
  SnapshotWrapper,
  DownloadButton,
} from "./styled";

// ── SVG building blocks ──

function FullBarsSvg({
  vector,
  maxValues,
  layout,
  color,
  vbH,
  idScope,
  svgRef,
  extraSvgProps,
}: {
  vector: (number | string)[];
  maxValues?: number[];
  layout: ReturnType<typeof computeLayout>;
  color: string;
  vbH: number;
  idScope: string;
  svgRef?: React.Ref<SVGSVGElement>;
  extraSvgProps?: React.SVGProps<SVGSVGElement>;
}) {
  const tt = useTooltip();
  return (
    <ResponsiveSvg
      ref={svgRef}
      viewBox={`0 0 ${VB_W} ${vbH}`}
      preserveAspectRatio="xMidYMid meet"
      onMouseLeave={tt.hide}
      {...extraSvgProps}
    >
      {extraSvgProps?.children}
      {renderGroupLabelsAndSeparators(layout)}
      {renderBars({
        vector,
        maxValues,
        layout,
        color,
        withGroupLabels: true,
        idScope,
        onTap: tt.tap,
        onHover: tt.show,
        onHoverEnd: tt.hide,
      })}
      {tt.tip && <TooltipOverlay tip={tt.tip} vbW={VB_W} vbH={vbH} />}
    </ResponsiveSvg>
  );
}

function CompactBarsSvg({
  vector,
  maxValues,
  layout,
  color,
  vbH,
  idScope,
  className,
  svgRef,
  extraSvgProps,
}: {
  vector: (number | string)[];
  maxValues?: number[];
  layout: ReturnType<typeof computeLayout>;
  color: string;
  vbH: number;
  idScope: string;
  className?: string;
  svgRef?: React.Ref<SVGSVGElement>;
  extraSvgProps?: React.SVGProps<SVGSVGElement>;
}) {
  const tt = useTooltip();
  return (
    <ResponsiveSvg
      ref={svgRef}
      viewBox={`0 0 ${VB_W} ${vbH}`}
      preserveAspectRatio="xMidYMid meet"
      className={className}
      onMouseLeave={tt.hide}
      onTouchStart={() => tt.hide()}
      {...extraSvgProps}
    >
      {extraSvgProps?.children}
      {renderBars({
        vector,
        maxValues,
        layout,
        color,
        withGroupLabels: false,
        idScope,
        onTap: tt.tap,
        onHover: tt.show,
        onHoverEnd: tt.hide,
      })}
      {tt.tip && <TooltipOverlay tip={tt.tip} vbW={VB_W} vbH={vbH} />}
    </ResponsiveSvg>
  );
}

function FullCompareSvg({
  vectorA,
  vectorB,
  maxValues,
  layout,
  colorA,
  colorB,
  labelA,
  labelB,
  vbH,
  idScope,
}: {
  vectorA: (number | string)[];
  vectorB: (number | string)[];
  maxValues?: number[];
  layout: ReturnType<typeof computeLayout>;
  colorA: string;
  colorB: string;
  labelA: string;
  labelB: string;
  vbH: number;
  idScope: string;
}) {
  const tt = useTooltip();
  return (
    <ResponsiveSvg
      viewBox={`0 0 ${VB_W} ${vbH}`}
      preserveAspectRatio="xMidYMid meet"
      onMouseLeave={tt.hide}
    >
      {renderGroupLabelsAndSeparators(layout)}
      {renderComparisonBars({
        vectorA,
        vectorB,
        maxValues,
        layout,
        colorA,
        colorB,
        labelA,
        labelB,
        withGroupLabels: true,
        idScope,
        onTap: tt.tap,
        onHover: tt.show,
        onHoverEnd: tt.hide,
      })}
      {tt.tip && <TooltipOverlay tip={tt.tip} vbW={VB_W} vbH={vbH} />}
    </ResponsiveSvg>
  );
}

function CompactCompareSvg({
  vectorA,
  vectorB,
  maxValues,
  layout,
  colorA,
  colorB,
  labelA,
  labelB,
  vbH,
  idScope,
  className,
}: {
  vectorA: (number | string)[];
  vectorB: (number | string)[];
  maxValues?: number[];
  layout: ReturnType<typeof computeLayout>;
  colorA: string;
  colorB: string;
  labelA: string;
  labelB: string;
  vbH: number;
  idScope: string;
  className?: string;
}) {
  const tt = useTooltip();
  return (
    <ResponsiveSvg
      viewBox={`0 0 ${VB_W} ${vbH}`}
      preserveAspectRatio="xMidYMid meet"
      className={className}
      onMouseLeave={tt.hide}
      onTouchStart={() => tt.hide()}
    >
      {renderComparisonBars({
        vectorA,
        vectorB,
        maxValues,
        layout,
        colorA,
        colorB,
        labelA,
        labelB,
        withGroupLabels: false,
        idScope,
        onTap: tt.tap,
        onHover: tt.show,
        onHoverEnd: tt.hide,
      })}
      {tt.tip && <TooltipOverlay tip={tt.tip} vbW={VB_W} vbH={vbH} />}
    </ResponsiveSvg>
  );
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path
        d="M7 1v8m0 0L4 6.5M7 9l3-2.5M2 11h10"
        stroke={theme.colors.textMuted}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Public components ──

export interface TxFingerprintProps {
  vector: (number | string)[];
  maxValues?: number[];
  width?: number;
  height?: number;
  color?: string;
  showLabels?: boolean;
  compact?: boolean;
  className?: string;
}

export function TxFingerprint({
  vector,
  maxValues,
  color = theme.colors.primary,
  showLabels = false,
  compact = false,
  className,
}: TxFingerprintProps) {
  const vbH = compact ? VB_H_COMPACT : showLabels ? VB_H_FULL : VB_H_COMPACT;
  const layout = computeLayout(
    vector.length,
    VB_W,
    vbH,
    showLabels && !compact,
  );

  if (!showLabels || compact) {
    return (
      <CompactBarsSvg
        vector={vector}
        maxValues={maxValues}
        layout={layout}
        color={color}
        vbH={vbH}
        idScope="c"
        className={className}
      />
    );
  }

  const compactLayout = computeLayout(vector.length, VB_W, VB_H_COMPACT, false);
  return (
    <div className={className}>
      <DesktopOnly>
        <FullBarsSvg
          vector={vector}
          maxValues={maxValues}
          layout={layout}
          color={color}
          vbH={vbH}
          idScope="d"
        />
      </DesktopOnly>
      <MobileOnly>
        <CompactBarsSvg
          vector={vector}
          maxValues={maxValues}
          layout={compactLayout}
          color={color}
          vbH={VB_H_COMPACT}
          idScope="m"
        />
      </MobileOnly>
    </div>
  );
}

export interface FingerprintCompareProps {
  vectorA: (number | string)[];
  vectorB: (number | string)[];
  maxValues?: number[];
  colorA?: string;
  colorB?: string;
  labelA?: string;
  labelB?: string;
  showLabels?: boolean;
  compact?: boolean;
  className?: string;
}

export function FingerprintCompare({
  vectorA,
  vectorB,
  maxValues,
  colorA = theme.colors.primary,
  colorB = theme.colors.accent,
  labelA = "This TX",
  labelB = "Similar",
  showLabels = false,
  compact = false,
  className,
}: FingerprintCompareProps) {
  const vbH = compact ? VB_H_COMPACT : showLabels ? VB_H_FULL : VB_H_COMPACT;
  const layout = computeLayout(
    vectorA.length,
    VB_W,
    vbH,
    showLabels && !compact,
  );

  if (!showLabels || compact) {
    return (
      <CompactCompareSvg
        vectorA={vectorA}
        vectorB={vectorB}
        maxValues={maxValues}
        layout={layout}
        colorA={colorA}
        colorB={colorB}
        labelA={labelA}
        labelB={labelB}
        vbH={vbH}
        idScope="cc"
        className={className}
      />
    );
  }

  const compactLayout = computeLayout(
    vectorA.length,
    VB_W,
    VB_H_COMPACT,
    false,
  );
  return (
    <div className={className}>
      <DesktopOnly>
        <FullCompareSvg
          vectorA={vectorA}
          vectorB={vectorB}
          maxValues={maxValues}
          layout={layout}
          colorA={colorA}
          colorB={colorB}
          labelA={labelA}
          labelB={labelB}
          vbH={vbH}
          idScope="cd"
        />
      </DesktopOnly>
      <MobileOnly>
        <CompactCompareSvg
          vectorA={vectorA}
          vectorB={vectorB}
          maxValues={maxValues}
          layout={compactLayout}
          colorA={colorA}
          colorB={colorB}
          labelA={labelA}
          labelB={labelB}
          vbH={VB_H_COMPACT}
          idScope="cm"
        />
      </MobileOnly>
    </div>
  );
}

export function useFingerprintSnapshot(
  svgRef: React.RefObject<SVGSVGElement | null>,
) {
  return useCallback(
    (scale = 2): Promise<string> =>
      new Promise((resolve, reject) => {
        const svg = svgRef.current;
        if (!svg) return reject(new Error("No SVG ref"));

        const svgData = new XMLSerializer().serializeToString(svg);
        const blob = new Blob(
          [`<?xml version="1.0" encoding="UTF-8"?>${svgData}`],
          { type: "image/svg+xml" },
        );
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = VB_W * scale;
          canvas.height = VB_H_FULL * scale;
          const ctx = canvas.getContext("2d")!;
          ctx.scale(scale, scale);
          ctx.drawImage(img, 0, 0, VB_W, VB_H_FULL);
          URL.revokeObjectURL(url);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = reject;
        img.src = url;
      }),
    [svgRef],
  );
}

export function SnapshotableFingerprint({
  vector,
  maxValues,
  color = theme.colors.primary,
  showLabels = true,
  label,
  className,
}: TxFingerprintProps & { label?: string }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const snapshot = useFingerprintSnapshot(svgRef);

  const handleDownload = useCallback(async () => {
    const dataUrl = await snapshot(3);
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = label ? `tx-fingerprint-${label}.png` : "tx-fingerprint.png";
    a.click();
  }, [snapshot, label]);

  const vbH = showLabels ? VB_H_FULL : VB_H_COMPACT;
  const layout = computeLayout(vector.length, VB_W, vbH, showLabels);
  const compactLayout = computeLayout(vector.length, VB_W, VB_H_COMPACT, false);

  return (
    <SnapshotWrapper className={className}>
      <DesktopOnly>
        <FullBarsSvg
          vector={vector}
          maxValues={maxValues}
          layout={layout}
          color={color}
          vbH={vbH}
          idScope="sd"
          svgRef={svgRef}
          extraSvgProps={{
            xmlns: "http://www.w3.org/2000/svg",
            children: (
              <rect
                width={VB_W}
                height={vbH}
                fill={theme.colors.bgCard}
                rx="8"
              />
            ),
          }}
        />
      </DesktopOnly>
      <MobileOnly>
        <CompactBarsSvg
          vector={vector}
          maxValues={maxValues}
          layout={compactLayout}
          color={color}
          vbH={VB_H_COMPACT}
          idScope="sm"
          extraSvgProps={{
            children: (
              <rect
                width={VB_W}
                height={VB_H_COMPACT}
                fill={theme.colors.bgCard}
                rx="8"
              />
            ),
          }}
        />
      </MobileOnly>
      <DownloadButton onClick={handleDownload} title="Download as PNG">
        <DownloadIcon />
      </DownloadButton>
    </SnapshotWrapper>
  );
}
